/**
 * Aba Comissao e cobranca: o editor da administracao no topo, os quatro fatos
 * da comissao (percentual, modelo, estornos, valor do periodo) e o extrato
 * mensal com total.
 *
 * Decisoes nao obvias:
 *  - Pedido do dono (25/09/2026): a comissao e ALTERADA aqui pela
 *    administracao (EditorDeComissao, componente compartilhado, no topo) e
 *    aparece na hora na aba da propria empresa. Os cartoes leem o cadastro VIVO
 *    (`useCompany`), nao o do detalhe carregado, para refletir a alteracao sem
 *    recarregar. O historico mensal nao muda: comissao ja apurada fica
 *    congelada (ADR-001).
 *  - O modelo de cobranca (contrato x automatico) NAO foi decidido pelo dono.
 *    A aba mostra o modelo registrado com a nota fixa de "decisao pendente".
 *  - A politica de estorno aparece com a explicacao decidida (reembolso nao
 *    reverte, chargeback reverte) e as regras gerais da comissao ficam
 *    escritas logo abaixo, inclusive a da venda fechada pela vendedora (E5).
 *  - O extrato e por MES que toca a janela do cabecalho; o status segue o
 *    ciclo a faturar -> faturado -> pago, que vale nos dois modelos.
 */

import { EditorDeComissao } from '../../../components/EditorDeComissao';
import { Badge, Footnote, Icon, Panel, SectionCard } from '../../../components/ui';
import { useCompany } from '../../../lib/companyContext';
import { formatBRLFromCents, formatPercentFromBps } from '../../../lib/format';
import { rotuloDoMes } from '../mock';
import type { CompanyDetail } from '../types';
import { MODELO_COBRANCA, NOTA_MODELO_PENDENTE, POLITICA_ESTORNO, POLITICA_PADRAO, REGRAS_COMISSAO, STATUS_FATURA } from '../vocabulario';
import { Delta } from './Delta';

export function AbaComissao({ detail }: { readonly detail: CompanyDetail }): JSX.Element {
  const { metrics, previous, commissionHistory } = detail;
  const { companies } = useCompany();
  const company = companies.find((c) => c.id === detail.company.id) ?? detail.company;
  const modelo = MODELO_COBRANCA[company.billingModel];
  const politica = company.refundPolicy ?? POLITICA_PADRAO;
  const infoPolitica = POLITICA_ESTORNO[politica];
  const totalRecuperado = commissionHistory.reduce((acc, m) => acc + m.recoveredAmountCents, 0);
  const totalComissao = commissionHistory.reduce((acc, m) => acc + m.commissionCents, 0);
  const mudouPercentual = commissionHistory.some((m) => m.feeBps !== company.feeBps);

  return (
    <div className="flex flex-col gap-4">
      <EditorDeComissao company={company} editable />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Panel className="flex flex-col gap-1 p-5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Percentual de comissão</span>
          <span className="text-2xl font-semibold tracking-tight text-ink">{formatPercentFromBps(company.feeBps)}</span>
          <span className="text-xs text-ink-subtle">Sobre o valor pago de cada venda recuperada, congelado por conversão.</span>
        </Panel>
        <Panel className="flex flex-col gap-2 p-5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Modelo de cobrança</span>
          <span>
            <Badge tone={modelo.tone}>{modelo.label}</Badge>
          </span>
          <span className="text-xs text-ink-subtle">{modelo.explicacao}</span>
        </Panel>
        <Panel className="flex flex-col gap-2 p-5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Estornos</span>
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={infoPolitica.tone}>{infoPolitica.curto}</Badge>
            {politica === POLITICA_PADRAO ? <span className="text-[11px] text-ink-subtle">padrão decidido</span> : null}
          </span>
          <span className="text-xs text-ink-subtle">{infoPolitica.explica}</span>
        </Panel>
        <Panel className="flex flex-col gap-1 p-5">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão no período</span>
          <span className="text-2xl font-semibold tracking-tight text-ink">{formatBRLFromCents(metrics.commissionCents)}</span>
          <Delta atual={metrics.commissionCents} anterior={previous === null ? null : previous.commissionCents} tipo="dinheiro" />
          <span className="text-xs text-ink-subtle">Sobre {formatBRLFromCents(metrics.recoveredAmountCents)} recuperados na janela do cabeçalho.</span>
        </Panel>
      </div>

      <Footnote>{NOTA_MODELO_PENDENTE}</Footnote>

      <SectionCard id="comissao-regras" title="Regras que valem para esta empresa" icon="escudo" description="Decididas pelo dono (ADR-001). Mudam só com nova decisão escrita, nunca por tela.">
        <ul className="grid gap-2 md:grid-cols-2">
          {REGRAS_COMISSAO.map((regra) => (
            <li key={regra} className="flex items-start gap-2 text-sm text-ink-muted">
              <Icon name="check" size="sm" className="mt-0.5 shrink-0 text-status-good" />
              <span>{regra}</span>
            </li>
          ))}
          <li className="flex items-start gap-2 text-sm text-ink-muted">
            <Icon name="check" size="sm" className="mt-0.5 shrink-0 text-status-good" />
            <span>
              Estornos nesta empresa: <strong className="text-ink">{infoPolitica.label}</strong>. Reembolso parcial nunca reverte.
            </span>
          </li>
        </ul>
      </SectionCard>

      <SectionCard
        id="comissao-extrato"
        title="Histórico mensal"
        icon="comissao"
        description="Um mês por linha, do mais recente para o mais antigo, entre os meses que a janela do cabeçalho alcança."
        padded={false}
      >
        {commissionHistory.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-ink-muted">A janela não alcança nenhum mês depois da criação da empresa. Escolha um período maior no cabeçalho.</p>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-left text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
                    <th scope="col" className="px-5 py-2">
                      Mês
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Recuperado
                    </th>
                    <th scope="col" className="px-3 py-2 text-right">
                      Comissão
                    </th>
                    <th scope="col" className="px-3 py-2">
                      Percentual
                    </th>
                    <th scope="col" className="px-5 py-2">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {commissionHistory.map((mes) => {
                    const status = STATUS_FATURA[mes.status];
                    return (
                      <tr key={mes.month}>
                        <th scope="row" className="px-5 py-3 text-left font-medium text-ink">
                          {rotuloDoMes(mes.month)}
                        </th>
                        <td className="px-3 py-3 text-right tabular-nums text-ink">{formatBRLFromCents(mes.recoveredAmountCents)}</td>
                        <td className="px-3 py-3 text-right tabular-nums text-ink">{formatBRLFromCents(mes.commissionCents)}</td>
                        <td className="px-3 py-3 tabular-nums text-ink-muted">{formatPercentFromBps(mes.feeBps)}</td>
                        <td className="px-5 py-3">
                          <Badge tone={status.tone} dot>
                            {status.label}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-line bg-elevated text-xs">
                    <th scope="row" className="px-5 py-3 text-left font-medium text-ink-muted">
                      Total dos meses listados
                    </th>
                    <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatBRLFromCents(totalRecuperado)}</td>
                    <td className="px-3 py-3 text-right font-medium tabular-nums text-ink">{formatBRLFromCents(totalComissao)}</td>
                    <td className="px-3 py-3" colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>

            <ul className="divide-y divide-line md:hidden">
              {commissionHistory.map((mes) => {
                const status = STATUS_FATURA[mes.status];
                return (
                  <li key={mes.month} className="flex flex-col gap-2 px-5 py-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-ink">{rotuloDoMes(mes.month)}</p>
                      <Badge tone={status.tone} dot>
                        {status.label}
                      </Badge>
                    </div>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Recuperado</dt>
                        <dd className="tabular-nums text-ink">{formatBRLFromCents(mes.recoveredAmountCents)}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão ({formatPercentFromBps(mes.feeBps)})</dt>
                        <dd className="tabular-nums text-ink">{formatBRLFromCents(mes.commissionCents)}</dd>
                      </div>
                    </dl>
                  </li>
                );
              })}
              <li className="flex items-center justify-between gap-2 bg-elevated px-5 py-3 text-sm">
                <span className="text-ink-muted">Total</span>
                <span className="tabular-nums font-medium text-ink">
                  {formatBRLFromCents(totalComissao)} <span className="text-ink-subtle">sobre {formatBRLFromCents(totalRecuperado)}</span>
                </span>
              </li>
            </ul>
          </>
        )}
        {mudouPercentual ? (
          <div className="px-5 pb-5 pt-3">
            <Footnote>
              O percentual atual ({formatPercentFromBps(company.feeBps)}) difere do aplicado nos meses acima: comissão já apurada fica congelada; o novo percentual vale para as próximas
              vendas recuperadas.
            </Footnote>
          </div>
        ) : null}
      </SectionCard>
    </div>
  );
}
